import { BIndexThresholdType } from '@report/graphql';
import {
  Box,
  Chip,
  Flex,
  FormNumberInput,
  FormRadioSelection,
  FormSearchMultipleSelection,
  FormSearchSelection,
  Grid,
  Text,
  type Theme,
} from '@shared/components';

interface ItemAnalysisOtherFilterFormFieldProps {
  qBankStructuresOptions?: {
    label: string;
    value: string;
  }[];
  showQuestionStatusPlaceholder?: boolean;
  questionStatusOptions?: {
    label: string;
    value: string;
  }[];
  disableKnowledgeGroupCustomThreshold: boolean;
  disableNonKnowledgeGroupCustomThreshold: boolean;
  questionStatusSelected?: string[];
  showKnowledgeGroupThresholdError?: boolean;
  showNonKnowledgeGroupThresholdError?: boolean;
  getAllQBankStructuresLoading?: boolean;
}

const statusSelectedColorMap: Record<string, (theme: Theme) => string> = {
  PUBLISHED: (theme) => theme.palette.success.dark,
  UNPUBLISHED: (theme) => theme.palette.warning.dark,
  DISABLED: (theme) => theme.palette.grey[700],
};

const statusColorMap: Record<string, (theme: Theme) => string> = {
  PUBLISHED: (theme) => theme.palette.success.lighter ?? '',
  UNPUBLISHED: (theme) => theme.palette.warning.lighter ?? '',
  DISABLED: (theme) => theme.palette.grey[50] ?? '',
};

const statusBorderColorMap: Record<string, (theme: Theme) => string> = {
  PUBLISHED: (theme) => theme.palette.success.main,
  UNPUBLISHED: (theme) => theme.palette.warning.main,
  DISABLED: (theme) => theme.palette.grey[500],
};

export default function ItemAnalysisOtherFilterFormField(
  props: Readonly<ItemAnalysisOtherFilterFormFieldProps>,
) {
  const {
    qBankStructuresOptions = [],
    questionStatusOptions = [],
    disableKnowledgeGroupCustomThreshold,
    disableNonKnowledgeGroupCustomThreshold,
    questionStatusSelected = [],
    showKnowledgeGroupThresholdError,
    showNonKnowledgeGroupThresholdError,
    getAllQBankStructuresLoading,
  } = props;

  return (
    <Grid
      sx={(theme) => ({
        columnGap: theme.spacing(6),
        rowGap: theme.spacing(6),
        gridTemplateColumns: '160px 1fr',
      })}
    >
      <Text variant='body1Semibold'>Other Filters:</Text>
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(3) }}>
        <FormSearchSelection
          name='examCourse'
          label='คลังข้อสอบ'
          placeholder='เลือกคลังข้อสอบ'
          options={qBankStructuresOptions}
          loading={getAllQBankStructuresLoading}
        />
        <Flex sx={{ gap: (theme) => theme.spacing(3) }}>
          <FormSearchMultipleSelection
            name='questionStatus'
            label='สถานะคำถาม'
            placeholder={questionStatusSelected.length === 0 ? 'เลือกสถานะคำถาม' : ''}
            options={questionStatusOptions}
            limitTags={3}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const status = typeof option.value === 'string' ? option.value : undefined;
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option.value}-chip`}
                    label={option.label}
                    sx={(theme) => ({
                      alignItems: 'center',
                      padding: theme.spacing(0.5, 1, 0.5, 2),
                      borderRadius: theme.shape.rounded(12.5),
                      bgcolor: (status && statusColorMap[status]) ?? undefined,
                      color: (status && statusSelectedColorMap[status]?.(theme)) ?? undefined,
                      border: `
                          1px solid ${
                            (status && statusBorderColorMap[status]?.(theme)) ?? undefined
                          }
                        `,
                      '.MuiSvgIcon-root': {
                        fontSize: 16,
                        color: (status && statusSelectedColorMap[status]?.(theme)) ?? undefined,
                        '&:hover': {
                          color: (status && statusBorderColorMap[status]?.(theme)) ?? undefined,
                        },
                      },
                    })}
                  />
                );
              })
            }
            sx={{ width: '100%' }}
          />
          <FormNumberInput
            name='minExaminees'
            label='จำนวนผู้เข้าสอบขั้นต่ำ'
            placeholder='ระบุจำนวนผู้เข้าสอบขั้นต่ำ'
            helperText='จำนวนขั้นต่ำ 300 คน (กรอก 0 เพื่อเลือกทั้งหมด)'
            sx={{
              width: '100%',
            }}
          />
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(3) }}>
          <Text variant='body2Semibold'>เกณฑ์การคำนวณ B-Index</Text>
          <Flex sx={{ gap: (theme) => theme.spacing(3) }}>
            <Box
              sx={(theme) => ({
                p: theme.spacing(4),
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
              })}
            >
              <FormRadioSelection
                name='bIndex.knowledgeGroup.knowledgeGroupSelection'
                label='กลุ่มรอบรู้'
                options={[
                  {
                    label: 'ผ่านตามเกณฑ์ที่กำหนด',
                    value: BIndexThresholdType.UseExamResult,
                  },
                  {
                    label: (
                      <Flex
                        sx={{
                          alignItems: 'center',
                          gap: (theme) => theme.spacing(2),
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Text sx={{ flexShrink: 0 }}>กำหนดเกณฑ์ใหม่</Text>
                        <FormNumberInput
                          name='bIndex.knowledgeGroup.customThreshold'
                          disabled={disableKnowledgeGroupCustomThreshold}
                          sx={{ maxWidth: '80px' }}
                          slotProps={{ htmlInput: { sx: { textAlign: 'center' } } }}
                          max={100}
                        />
                        <Text sx={{ flexShrink: 0 }}>%</Text>
                      </Flex>
                    ),
                    value: BIndexThresholdType.CustomThreshold,
                  },
                ]}
              />
              <Text
                variant='caption'
                sx={{
                  color: (theme) => theme.palette.error.main,
                  visibility: showKnowledgeGroupThresholdError ? 'visible' : 'hidden',
                }}
              >
                กรุณาระบุเปอร์เซ็นต์ผ่านใหม่
              </Text>
            </Box>
            <Box
              sx={(theme) => ({
                p: theme.spacing(4),
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
              })}
            >
              <FormRadioSelection
                name='bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection'
                label='กลุ่มไม่รอบรู้'
                options={[
                  {
                    label: 'ไม่ผ่านตามเกณฑ์ที่กำหนด',
                    value: BIndexThresholdType.UseExamResult,
                  },
                  {
                    label: (
                      <Flex
                        sx={{
                          alignItems: 'center',
                          gap: (theme) => theme.spacing(2),
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Text sx={{ flexShrink: 0 }}>กำหนดเกณฑ์ใหม่</Text>
                        <FormNumberInput
                          name='bIndex.nonKnowledgeGroup.customThreshold'
                          disabled={disableNonKnowledgeGroupCustomThreshold}
                          sx={{ maxWidth: '80px' }}
                          slotProps={{ htmlInput: { sx: { textAlign: 'center' } } }}
                          max={100}
                        />
                        <Text sx={{ flexShrink: 0 }}>%</Text>
                      </Flex>
                    ),
                    value: BIndexThresholdType.CustomThreshold,
                  },
                ]}
              />
              <Text
                variant='caption'
                sx={{
                  color: (theme) => theme.palette.error.main,
                  visibility: showNonKnowledgeGroupThresholdError ? 'visible' : 'hidden',
                }}
              >
                กรุณาระบุเปอร์เซ็นต์ผ่านใหม่
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Grid>
  );
}
